import React from "react";
import { useNavigate } from "react-router-dom";

import { type IconDefinition, faClock, faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { PageBanner } from "@performyard/py-components";
import {
	MAX_MEETING_TRANSCRIPT_LENGTH,
	VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES,
} from "@performyard/service-contracts";

import {
	getVideoConferencingProviderShortName,
	useDismissTranscriptBanner,
	useRbacObjectPermissions,
	useRetrieveMeetingTranscript,
	useUserVideoConferencingStatus,
} from "../../../../api/hooks";
import { type TranscriptBannerStateKind, useTranscriptBannerState } from "../hooks";

import { useMeetingContext } from "../context";

const MEETINGS_INTEGRATIONS_SETTINGS_PATH = "/settings/meetings-integrations";

// The design system's small button has a 50px min-width that py-components' ButtonStandard doesn't
// implement, so short labels render too narrow without it.
const BUTTON_MIN_WIDTH_CLASS = "min-w-[50px]";

const MAX_TRANSCRIPT_CHARACTERS = MAX_MEETING_TRANSCRIPT_LENGTH.toLocaleString("en-US");

type BannerConfig = {
	color: "primary" | "red";
	icon: IconDefinition;
	mainTitle: string;
	subTitle?: string;
	/**
	 * The banner's right-hand action. "retrieve" pulls the transcript from the provider and renders only
	 * when the creator's connection can serve one; "settings" always renders. `sentence` closes the
	 * subtitle and drops with the button, so the copy never points at a trigger that isn't there.
	 */
	action?: { label: string; kind: "retrieve" | "settings"; sentence?: string };
};

// Same promise before (scheduled) and after (awaiting) the meeting ends — one banner, one dismissal.
const AWAITING_BANNER: BannerConfig = {
	color: "primary",
	icon: faClock,
	mainTitle: "Transcript arrives automatically",
	subTitle: "It'll appear here shortly after the meeting ends.",
};

// Copy + treatment per state, from the PY-12435 design. "none" has no banner.
const BANNER_BY_STATE: Partial<Record<TranscriptBannerStateKind, BannerConfig>> = {
	scheduled: AWAITING_BANNER,
	awaiting: AWAITING_BANNER,
	too_long: {
		color: "red",
		icon: faTriangleExclamation,
		// Single-line title (no subtitle) per the PY-12435 design.
		mainTitle: `Transcripts have a maximum length of ${MAX_TRANSCRIPT_CHARACTERS} characters. We saved the first ${MAX_TRANSCRIPT_CHARACTERS} characters.`,
	},
	failed: {
		color: "red",
		icon: faTriangleExclamation,
		mainTitle: "Unable to retrieve transcript",
		subTitle: "An error occurred in retrieving the transcript",
		// Something did fail here, so the trigger reads as a retry.
		action: { label: "Retry", kind: "retrieve" },
	},
	// Deliberately not red: the transcript is overdue, not known-failed. The provider may still be
	// processing, so this offers the user control without claiming something broke.
	delayed: {
		color: "primary",
		icon: faClock,
		mainTitle: "Transcript delayed",
		subTitle: "Transcripts usually arrive in a few minutes and will appear here automatically.",
		// Nothing failed, so the press expedites the check rather than re-running anything.
		action: { label: "Check Now", kind: "retrieve", sentence: "You can check now, or paste it below." },
	},
	pmi: {
		color: "primary",
		icon: faTriangleExclamation,
		mainTitle: "Automatic Transcript Retrieval Off",
		subTitle: `${VIDEO_CONFERENCING_PROVIDER_DISPLAY_NAMES.zoom.long} is connected, but transcripts can't be retrieved automatically because your organization's meetings use Personal Meeting IDs.`,
	},
	disconnected: {
		color: "primary",
		icon: faTriangleExclamation,
		// Fallback only — the render swaps in the meeting's provider name ("Zoom was disconnected").
		mainTitle: "Video conferencing was disconnected",
		subTitle: "Transcripts no longer arrive automatically. Reconnect in Settings, or paste the transcript below.",
	},
	video_conferencing_disabled: {
		color: "primary",
		icon: faTriangleExclamation,
		mainTitle: "Transcript unavailable",
		subTitle:
			"The transcript for this meeting is unavailable because video conferencing has been disabled by your organization.",
	},
	discoverability: {
		color: "primary",
		icon: faTriangleExclamation,
		mainTitle: "Get transcripts automatically",
		subTitle:
			"Connect your video conferencing tool and transcripts will appear here automatically after your meeting ends.",
		action: { label: "Connect in Settings", kind: "settings" },
	},
};

/**
 * Dismissable status banner shown above the meeting transcript box (meeting-creator only); the state comes
 * from useTranscriptBannerState. Per the PY-12505 design these banners are dismissable and the
 * delayed/failed ones host the manual retrieval trigger, so dismissing one also gives up the trigger; the
 * status chip stays either way.
 */
const TranscriptIngestBanner = (): React.ReactElement | null => {
	const { meeting } = useMeetingContext();
	const state = useTranscriptBannerState();
	const { mutate: dismissBanner } = useDismissTranscriptBanner();
	const navigate = useNavigate();
	const { mutate: retrieveTranscript, isPending: isRetrieving } = useRetrieveMeetingTranscript();
	const action = BANNER_BY_STATE[state]?.action;
	const { data: vcStatus } = useUserVideoConferencingStatus({ enabled: action?.kind === "retrieve" });
	const { data: rbacPermissions } = useRbacObjectPermissions("meeting", meeting._id);

	if (state === "none") {
		return null;
	}

	const config = BANNER_BY_STATE[state];
	// "scheduled" shares the awaiting banner and its dismissal key, so one dismissal covers both phases.
	const dismissKey = state === "scheduled" ? "awaiting" : state;
	if (!config || meeting.dismissedTranscriptBanners?.includes(dismissKey)) {
		return null;
	}

	const providerName = getVideoConferencingProviderShortName(meeting.videoConference?.provider);
	const mainTitle = state === "disconnected" && providerName ? `${providerName} was disconnected` : config.mainTitle;

	// Retrieval runs on the creator's own grant, and a grant's scopes are fixed once issued, so the trigger
	// is withheld rather than offered as a button that would be refused.
	const canRetrieve =
		(rbacPermissions?.includes("meetings:retrieveTranscript") ?? false) &&
		vcStatus?.connections.find((candidate) => candidate.provider === meeting.videoConference?.provider)
			?.canRetrieveTranscriptOnDemand === true;

	const rightActionButton = !action
		? undefined
		: action.kind === "settings"
			? {
					children: action.label,
					onClick: () => navigate(MEETINGS_INTEGRATIONS_SETTINGS_PATH),
					dangerouslySetClassName: BUTTON_MIN_WIDTH_CLASS,
				}
			: canRetrieve
				? {
						children: action.label,
						loading: isRetrieving,
						disabled: isRetrieving,
						"aria-busy": isRetrieving,
						onClick: () => retrieveTranscript({ meetingId: meeting._id }),
						dangerouslySetClassName: BUTTON_MIN_WIDTH_CLASS,
					}
				: undefined;

	const subTitle =
		[config.subTitle, rightActionButton ? action?.sentence : undefined].filter(Boolean).join(" ") || undefined;

	return (
		<PageBanner
			color={config.color}
			size="small"
			icon={config.icon}
			mainTitle={mainTitle}
			subTitle={subTitle}
			onDismiss={() => dismissBanner({ meetingId: meeting._id, bannerKey: dismissKey })}
			rightActionButton={rightActionButton}
			data-testid={`transcript-banner-${state}`}
		/>
	);
};

export default TranscriptIngestBanner;
